import { useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Card, Row, Col, ListGroup, Badge, Stack } from 'react-bootstrap';
import { toast } from 'react-toastify';

// Orders
import {
  useGetGuestOrderDetailsQuery,
  useGetOrderDetailsQuery,
} from '../slices/ordersApiSlice';

// PDFs
import { useGenerateGuestInvoiceMutation } from '../slices/invoiceApiSlice';
import { useGenerateQuoteForOrderMutation } from '../slices/quotesApiSlice';
import { getApiBase } from '../utils/apiBase';

export default function GuestOrderSuccess() {
  const { id: orderId } = useParams();

  const API_BASE = getApiBase();

  // Public (guest) first; if 403, fall back to protected
  const pub = useGetGuestOrderDetailsQuery(orderId, { skip: !orderId });
  const needPrivate = !!orderId && pub.isError && pub.error?.status === 403;
  const priv = useGetOrderDetailsQuery(orderId, { skip: !needPrivate });

  const order     = pub.data || priv.data;
  const isLoading = pub.isLoading || priv.isLoading;
  const isError   = (pub.isError && pub.error?.status !== 403) || priv.isError;
  const error     = (pub.isError && pub.error?.status !== 403) ? pub.error : priv.error;

  const refetch = useCallback(() => {
    pub.refetch();
    if (needPrivate) priv.refetch();
  }, [pub, priv, needPrivate]);

  const [generateGuestInvoice, { isLoading: genInvoiceLoading }] =
    useGenerateGuestInvoiceMutation();
  const [generateQuoteForOrder, { isLoading: genQuoteLoading }] =
    useGenerateQuoteForOrderMutation();

  // -------- derived flags / helpers --------
  const isBusy   = genInvoiceLoading || genQuoteLoading;
  const isPaid   = !!order?.isPaid;
  const totalPrice = Number(order?.totalPrice ?? 0);
  const itemsCount = Array.isArray(order?.orderItems)
    ? order.orderItems.reduce((sum, item) => sum + Number(item.qty || 0), 0)
    : 0;

  const invoiceNumber =
    order?.documentRefs?.invoiceNumber || order?.invoice || null;

  const orderRef =
    order?.reference ||
    order?.orderRef ||
    (orderId ? orderId.slice(-6).toUpperCase() : '—');

  const quoteNumber =
    order?.documentRefs?.quoteNumber ||
    order?.quoteNumber ||
    order?.quote?.number ||
    null;

  const quotePdfUrl =
    order?.quotePdfUrl ||
    order?.documentRefs?.quotePdfUrl ||
    null;

  // If paid, always offer invoice (we can generate on demand)
  const shouldOfferInvoice = isPaid;
  const payOnPickup =
    order?.paymentMethod === 'PAY_ON_PICKUP' ||
    order?.paymentMethod === 'Pay on Pickup';

  const title = useMemo(() => {
    if (shouldOfferInvoice) return invoiceNumber ? `Invoice ${invoiceNumber}` : 'Invoice';
    if (quoteNumber) return `Quote ${quoteNumber}`;
    return `Quote for Order ${orderRef}`;
  }, [shouldOfferInvoice, invoiceNumber, quoteNumber, orderRef]);

  const absolutize = useCallback(
    (maybeRelative) => {
      if (!maybeRelative) return '';
      if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
      if (maybeRelative.startsWith('/')) return `${API_BASE}${maybeRelative}`;
      return `${API_BASE}/${maybeRelative.replace(/^\/+/, '')}`;
    },
    [API_BASE]
  );

  const openNew = useCallback(
    (url) => {
      const abs = absolutize(url);
      if (!abs) return;
      const final = abs.includes('?') ? `${abs}&t=${Date.now()}` : `${abs}?t=${Date.now()}`;
      window.open(final, '_blank', 'noopener,noreferrer');
    },
    [absolutize]
  );

  const downloadInvoice = useCallback(async () => {
    try {
      if (!orderId) return;
      // Ensure server builds PDF & returns canonical URLs (idempotent)
      const res = await generateGuestInvoice({ orderId }).unwrap();

      const url =
        res?.absolutePublicDownloadUrl ||
        res?.publicDownloadUrl ||
        (res?.invoiceNumber
          ? `/api/invoices/${encodeURIComponent(res.invoiceNumber)}/pdf/public`
          : null);

      if (!url) throw new Error('Invoice URL is missing.');
      openNew(url);

      // Refresh page data so the invoice number shows immediately
      refetch();
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Failed to prepare invoice.');
    }
  }, [orderId, generateGuestInvoice, openNew, refetch]);

  const downloadQuote = useCallback(async () => {
    try {
      if (quotePdfUrl) {
        openNew(quotePdfUrl);
        return;
      }
      if (quoteNumber) {
        openNew(`/api/quotes/by-number/${encodeURIComponent(quoteNumber)}/pdf/public`);
        return;
      }
      const res = await generateQuoteForOrder({ orderId }).unwrap();

      const num =
        res?.quote?.number ||
        res?.number ||
        res?.quoteNumber ||
        null;

      const url =
        res?.absoluteDownloadUrl ||
        res?.publicDownloadUrl ||
        (num ? `/api/quotes/by-number/${encodeURIComponent(num)}/pdf/public` : null);

      if (!url) throw new Error('Quote URL is missing from the response.');
      openNew(url);
      refetch();
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Failed to prepare quote.');
    }
  }, [quotePdfUrl, quoteNumber, generateQuoteForOrder, openNew, orderId, refetch]);

  const handleDownload = useCallback(() => {
    if (isBusy) return;
    return shouldOfferInvoice ? downloadInvoice() : downloadQuote();
  }, [isBusy, shouldOfferInvoice, downloadInvoice, downloadQuote]);

  // ---- Show error toast only once per distinct error ----
  const lastErrorKeyRef = useRef(null);
  useEffect(() => {
    if (!isError) return;
    const msg = error?.data?.message || 'Could not load order.';
    const key = `${error?.status || 'x'}:${msg}`;
    if (lastErrorKeyRef.current !== key) {
      lastErrorKeyRef.current = key;
      toast.error(msg);
    }
  }, [isError, error]);

  // -------- render --------
  if (isLoading) {
    return (
      <div className="py-4">
        <Card className="shadow-sm border-0">
          <Card.Body className="py-5 text-center">
            <h1 className="mb-2">Thanks for your order!</h1>
            <p className="text-muted mb-0">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading your confirmation…
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const btnLabel = shouldOfferInvoice ? 'Download Invoice (PDF)' : 'Download Quote (PDF)';

  return (
    <div className="py-4">
      <Row className="g-4">
        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <Stack direction="horizontal" className="justify-content-between flex-wrap gap-2">
                <div>
                  <h1 className="mb-1">Thanks for your order!</h1>
                  <div className="text-muted">
                    We’ve sent a confirmation email with your details.
                  </div>
                </div>
                <Badge bg={isPaid ? 'success' : 'warning'} className="fs-6">
                  {isPaid ? 'Payment received' : 'Payment pending'}
                </Badge>
              </Stack>

              <div className="mt-4">
                <p className="mb-2">
                  {shouldOfferInvoice ? (
                    <>
                      {invoiceNumber
                        ? <>Your invoice number is <strong>{invoiceNumber}</strong>.</>
                        : <>Your payment was received. Click below to download your invoice.</>}
                    </>
                  ) : quoteNumber ? (
                    <>Your Quote Number is <strong>{quoteNumber}</strong> (Order Ref:&nbsp;<strong>{orderRef}</strong>).</>
                  ) : (
                    <>Your reference is <strong>{orderRef}</strong>.</>
                  )}
                </p>
                {!isPaid && (
                  <Alert variant="info" className="mb-0">
                    {payOnPickup
                      ? 'You chose Pay on Pickup. Bring this quote to the store to pay and receive your final invoice.'
                      : 'Payment is not completed yet. You can download a quote now.'}
                  </Alert>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0 mt-4">
            <Card.Body className="p-4">
              <h5 className="mb-3">Next steps</h5>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <Button
                  variant="primary"
                  onClick={handleDownload}
                  disabled={isBusy || isLoading}
                  title={title}
                >
                  {isBusy ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Preparing…
                    </>
                  ) : (
                    btnLabel
                  )}
                </Button>
                <Button variant="outline-secondary" as={Link} to="/">
                  Continue Shopping
                </Button>
                <Button variant="link" className="ms-1" onClick={refetch}>
                  Refresh
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <h5 className="mb-3">Order summary</h5>
              <ListGroup variant="flush">
                <ListGroup.Item className="px-0 d-flex justify-content-between">
                  <span className="text-muted">Order Ref</span>
                  <span className="fw-semibold">{orderRef}</span>
                </ListGroup.Item>
                {invoiceNumber && (
                  <ListGroup.Item className="px-0 d-flex justify-content-between">
                    <span className="text-muted">Invoice</span>
                    <span className="fw-semibold">{invoiceNumber}</span>
                  </ListGroup.Item>
                )}
                <ListGroup.Item className="px-0 d-flex justify-content-between">
                  <span className="text-muted">Items</span>
                  <span className="fw-semibold">{itemsCount}</span>
                </ListGroup.Item>
                <ListGroup.Item className="px-0 d-flex justify-content-between">
                  <span className="text-muted">Total</span>
                  <span className="fw-semibold">${totalPrice.toFixed(2)}</span>
                </ListGroup.Item>
              </ListGroup>
              <div className="mt-3 text-muted small">
                Need help? Contact support with your order reference.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
